import React from 'react';
import { Box } from '@mui/material';
import { MetricCard } from '../../../ui-kit';
import { formatValue, toSparklinePoints } from '../format';
import type { KpiMetric } from '../../../types/manualDashboard';

/**
 * Headline figures for the whole selected range, each over its own trend.
 *
 * No period-on-period change on this board: the sparkline under each figure
 * already shows how it moved, cohort by cohort, with unsettled cohorts
 * marked — a single "vs last month" figure would say less, and could compare
 * a settled cohort with one still settling.
 */
export default function KpiRow({ metrics, loading }: { metrics: KpiMetric[]; loading?: boolean }) {
  if (!metrics?.length) return null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(3, minmax(0, 1fr))',
          lg: `repeat(${metrics.length}, minmax(0, 1fr))`,
        },
        gap: 1.5,
      }}
    >
      {metrics.map((metric) => {
        const format = (v: number | null) => formatValue(v, metric.format);
        return (
          <MetricCard
            key={metric.key}
            label={metric.label}
            value={format(metric.value)}
            info={metric.definition ?? undefined}
            loading={loading}
            trend={
              metric.sparkline
                ? {
                    points: toSparklinePoints(metric.sparkline),
                    formatValue: format,
                    label: `${metric.label} by cohort`,
                  }
                : null
            }
          />
        );
      })}
    </Box>
  );
}
